import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import BackButton from '@/components/ui/BackButton/BackButton.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import useRestaurants from '@/api/useRestaurants.ts';
import IntegrationPlatform, { allIntegrationPlatforms } from '@/_constants/IntegrationPlatform.ts';
import type { IntegrationPlatform as IntegrationPlatformT } from '@/_constants/IntegrationPlatform.ts';
import useIntegrationAdd from '@/api/useIntegrationAdd.ts';

export const schema = z
  .object({
    restaurant_id: z
      .string()
      .min(1, { message: 'This field is required.' })
      .max(50, { message: 'Must be less than 50 characters.' }),
    platform_name: z.enum(allIntegrationPlatforms, { message: 'Invalid playback speed value' }),
    api_key: z
      .string()
      .min(1, { message: 'This field is required.' })
      .max(300, { message: 'Must be less than 300 characters.' }),
    secret_token: z
      .string()
      .min(1, { message: 'This field is required.' })
      .max(300, { message: 'Must be less than 300 characters.' }),
    venue_group_id: z.string().max(300, { message: 'Must be less than 300 characters.' }).nullish(),
    venue_id: z.string().max(300, { message: 'Must be less than 300 characters.' }).nullish(),
  })
  .refine(
    (data) => {
      if (data.platform_name === IntegrationPlatform.SEVEN_ROOMS) {
        return !!data.venue_id;
      }
      return true;
    },
    { message: 'Venue ID is required for Seven Rooms integration', path: ['venue_id'] }
  );

export type Schema = z.infer<typeof schema>;

type AddIntegrationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
};
function AddIntegrationModal({ isOpen, onClose, onConfirm }: AddIntegrationModalProps) {
  const { t } = useTranslation();
  const { data: restaurants, isLoading: isLoadingRestaurants } = useRestaurants();
  const {
    mutateAsync: addIntegration,
    isError: isErrorAddIntegration,
    isPending: isLoadingAddIntegration,
    isSuccess: isSuccessAddIntegration,
    error: errorAddIntegration,
  } = useIntegrationAdd();

  const { handleSubmit, control, watch } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      restaurant_id: '',
      platform_name: '' as unknown as IntegrationPlatformT,
      api_key: '',
      secret_token: '',
      venue_group_id: '',
      venue_id: '',
    },
  });

  const handleAddIntegration = ({ restaurant_id, ...rest }: Schema) => {
    void (async () => {
      try {
        if (isLoadingAddIntegration) {
          return;
        }

        await addIntegration({
          ...rest,
          restaurant_id: parseInt(restaurant_id),
        });

        if (onConfirm) {
          onConfirm();
        }

        onClose();
      } catch (error) {
        console.error('Error adding Integration: ', error);
      }
    })();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <form className="modal-form" onSubmit={handleSubmit(handleAddIntegration)}>
        <DialogTitle>{t('modals.addIntegrationModal.title')}</DialogTitle>
        <DialogContent>
          <LoaderWrapper isLoading={isLoadingRestaurants}>
            <div className="pt-2 flex flex-col gap-4">
              <div className="flex flex-col gap-2 empty:hidden">
                {isSuccessAddIntegration && (
                  <Alert severity="success">{t('modals.addIntegrationModal.successMessage')}</Alert>
                )}
                {isErrorAddIntegration && <Alert severity="error">{errorAddIntegration?.message as string}</Alert>}
              </div>
              <div className="flex flex-col gap-4 w-full">
                <Controller
                  control={control}
                  name="restaurant_id"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <FormControl error={!!errors.restaurant_id}>
                      <InputLabel shrink id="restaurant_id">
                        {t('label.restaurant')}
                      </InputLabel>
                      <Select
                        value={value}
                        onChange={(event) => onChange(`${event.target.value}`)}
                        displayEmpty
                        variant="outlined"
                        label={t('label.restaurant')}
                        labelId="restaurant_id"
                        renderValue={(currentValue) =>
                          currentValue == '' ? (
                            <span className="text-[#a1a2a1]">{t('placeholder.restaurant')}</span>
                          ) : (
                            restaurants?.find((restaurant) => restaurant.id === parseInt(currentValue))?.name
                          )
                        }
                      >
                        {restaurants?.map((restaurant) => (
                          <MenuItem key={`restaurant_${restaurant.id}`} value={restaurant.id}>
                            {restaurant.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.restaurant_id?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="platform_name"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <FormControl error={!!errors.platform_name}>
                      <InputLabel shrink id="platform_name">
                        {t('label.integration')}
                      </InputLabel>
                      <Select
                        value={value}
                        onChange={(event) => onChange(`${event.target.value}`)}
                        displayEmpty
                        variant="outlined"
                        label={t('label.integration')}
                        labelId="platform_name"
                        renderValue={(currentValue) =>
                          !currentValue ? (
                            <span className="text-[#a1a2a1]">{t('placeholder.integration')}</span>
                          ) : (
                            currentValue
                          )
                        }
                      >
                        {allIntegrationPlatforms.map((integration) => (
                          <MenuItem key={`integration_${integration}`} value={integration}>
                            {integration}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.platform_name?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="api_key"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.api_key}
                      helperText={errors.api_key?.message}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.api_key')}
                      placeholder={t('placeholder.api_key')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="secret_token"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.secret_token}
                      helperText={errors.secret_token?.message}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.secret_token')}
                      placeholder={t('placeholder.secret_token')}
                    />
                  )}
                />
                {watch('platform_name') === IntegrationPlatform.SEVEN_ROOMS ? (
                  <>
                    <Controller
                      control={control}
                      name="venue_group_id"
                      render={({ field: { onChange, value }, formState: { errors } }) => (
                        <TextField
                          error={!!errors.venue_group_id}
                          helperText={errors.venue_group_id?.message}
                          value={value}
                          onChange={onChange}
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          variant="outlined"
                          label={t('label.venue_group_id')}
                          placeholder={t('placeholder.venue_group_id')}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="venue_id"
                      render={({ field: { onChange, value }, formState: { errors } }) => (
                        <TextField
                          error={!!errors.venue_id}
                          helperText={errors.venue_id?.message}
                          value={value}
                          onChange={onChange}
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          variant="outlined"
                          label={t('label.venue_id')}
                          placeholder={t('placeholder.venue_id')}
                        />
                      )}
                    />
                  </>
                ) : null}
              </div>
            </div>
          </LoaderWrapper>
        </DialogContent>
        <DialogActions>
          <div className="flex flex-wrap grow p-4 pt-0 justify-between">
            <BackButton onClick={onClose} autoFocus />
            <LoadingButton color="primary" variant="contained" loading={isLoadingAddIntegration} type="submit">
              {t('button.save')}
            </LoadingButton>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddIntegrationModal;
